import { formatMobile } from "../../../common/MobileField";
import { useEffect, useState } from "react";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { deleteBuilderService, getAllBuildersService, updateBuilderIsActiveService } from "../../../../Services/CRMAdminServices/CRMBuildersService";
import { getAllStates } from "../../../../Services/MMPClientServices/PropertySearchService";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
const customStyles = {
    head: {
        style: {
            fontWeight: '800',
            color: '#0000ff',
            fontSize: '14px'
        },
    },
};

// States column is capped at 40 characters wide (per spec) with the full
// list still available on hover via the title tooltip.
const STATES_DISPLAY_CHAR_LIMIT = 40;

// Builds the truncated display text by adding whole state names one at a
// time (never cutting a name in half) until the next name would push the
// text past the limit. This keeps the separators (", ") from silently
// eating into the visible character budget.
const buildTruncatedStatesText = (names: string[], limit: number) => {
    let result = '';
    for (let i = 0; i < names.length; i++) {
        const candidate = result ? `${result}, ${names[i]}` : names[i];
        if (candidate.length > limit) {
            if (!result) {
                // Even the very first name alone exceeds the limit; fall
                // back to a hard slice just for this one name.
                return `${names[i].slice(0, limit)}…`;
            }
            return `${result}…`;
        }
        result = candidate;
    }
    return result;
};

// Followup is stored/returned as an ISO date string; render it the same
// DD/MM/YYYY format used by the shared DatePicker component elsewhere in
// the app so the column reads consistently with the Add/Edit forms.
const formatFollowupDate = (value: any) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
   const dd = date.getDate().toString().padStart(2, "0");
const mm = (date.getMonth() + 1).toString().padStart(2, "0");
const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
};

const CRMBuilders = () => {
    const [builders, setBuilders] = useState<any[]>([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedBuilderId, setSelectedBuilderId] = useState<number | null>(null);
    const queryClient = useQueryClient();
    const navigate = useSafeNavigate();
    const [statesData, setStatesData] = useState([]);
    // Track current sort direction for each sortable button so a repeat
    // click reverses the order instead of re-applying the same sort.
    const [sortAZDirection, setSortAZDirection] = useState<'asc' | 'desc'>('asc');
    const [sortFollowupDirection, setSortFollowupDirection] = useState<'asc' | 'desc'>('asc');

    const { data: buildersData, isLoading, isError } = useGetRequestQuery<any>(
        ['getAllBuilders'],
        getAllBuildersService,
        { enabled: true }
    );

    useEffect(() => {
        if (buildersData && !isError && !isLoading) {
            const apiData = buildersData.data;
            setBuilders(Array.isArray(apiData) ? apiData : []);

        }
    }, [buildersData, isError, isLoading]);

    const { mutate: deleteBuilder, isPending: isDeleting } = useMutationRequestQuery(
        "deleteBuilder",
        deleteBuilderService,
        {
            onSuccess: () => {
                toast.success("Builder deleted successfully.");
                queryClient.invalidateQueries({ queryKey: ["getAllBuilders"] });
                setDeleteModal(false);
            },
            onError: () => {
                toast.error("Failed to delete builder.");
                setDeleteModal(false);
            },
        }
    );

    const confirmDelete = (id: number) => {
        setSelectedBuilderId(id);
        setDeleteModal(true);
    };

    const handleDeleteClick = () => {
        if (selectedBuilderId) {
            deleteBuilder(selectedBuilderId);
        }
    };

    const { mutate: updateIsActive } = useMutationRequestQuery(
        "updateBuilderIsActive",
        updateBuilderIsActiveService,
        {
            onSuccess: () => {
                toast.success("Builder status updated successfully.");
                queryClient.invalidateQueries({ queryKey: ["getAllBuilders"] });
            },
            onError: () => {
                toast.error("Failed to update builder status.");
            },
        }
    );

    const handleIsActiveChange = (supplierId: number, currentIsActive: number) => {
        const newIsActive = currentIsActive === 1 ? false : true;
        updateIsActive({ supplierId, isActive: newIsActive });
    };

    // Fetch all states
    const {
        data: allStatesData,
        isLoading: allStatesLoading,
        isError: allStatesIsError,
    } = useGetRequestQuery(
        ["getAllStates"],
        getAllStates,
        {
            enabled: true,
        }
    );

    // Transform the fetched states to { value, label } format
    useEffect(() => {
        if (allStatesData?.data && !allStatesIsError && !allStatesLoading) {
            const transformedData = allStatesData.data.map((state: any) => ({
                value: state.id,
                label: state.name,
            }));
            setStatesData(transformedData);
        }
    }, [allStatesData, allStatesIsError, allStatesLoading]);

    // Sort A - Z: alphabetical by Real Estate Builder (company) name.
    // Clicking again reverses the current direction (A-Z <-> Z-A).
    const handleSortAZ = () => {
        const nextDirection = sortAZDirection === 'asc' ? 'desc' : 'asc';
        setBuilders((prev) =>
            [...prev].sort((a, b) => {
                const comparison = (a.supplier || "").localeCompare(b.supplier || "", undefined, { sensitivity: "base" });
                return nextDirection === 'asc' ? comparison : -comparison;
            })
        );
        setSortAZDirection(nextDirection);
    };

    // Sort Followup: earliest follow-up date first; rows without a
    // follow-up date are pushed to the end rather than sorting to the top.
    // Clicking again reverses the current direction (earliest-first <-> latest-first).
    const handleSortFollowup = () => {
        const nextDirection = sortFollowupDirection === 'asc' ? 'desc' : 'asc';
        setBuilders((prev) =>
            [...prev].sort((a, b) => {
                const aTime = a.followUp ? new Date(a.followUp).getTime() : Number.POSITIVE_INFINITY;
                const bTime = b.followUp ? new Date(b.followUp).getTime() : Number.POSITIVE_INFINITY;
                const comparison = aTime - bTime;
                return nextDirection === 'asc' ? comparison : -comparison;
            })
        );
        setSortFollowupDirection(nextDirection);
    };

    // 4. Added ID column for visibility
    const columns = [
        {
            name: "Action",
            cell: (row: any) => (
                <ul className="action simple-list flex-row">
                    <li className="edit">
                        <Link to={`/EditBuilder/${row.supplierId}`}>
                            <i className="icon-pencil-alt" />
                        </Link>
                    </li>
                    <li className="delete">
                        <a onClick={() => confirmDelete(row.supplierId)} href="#">
                            <i className="icon-trash" />
                        </a>
                    </li>
                </ul>
            ),
            // style: { minWidth: '9rem' },
            width: '5rem'
        },
        {
            name: "Active",
            cell: (row: any) => (
                <input
                    type="checkbox"
                    checked={Number(row.isActive) === 1}
                    onChange={() => handleIsActiveChange(row.supplierId, Number(row.isActive))}
                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                />
            ),
            width: '7rem',
            center: true
        },
        {
            name: "Real Estate Builder",
            selector: (row: any) => row.supplier,
            sortable: false,
            width: '14rem'

        },
        {
            name: "Initials",
            selector: (row: any) => row.initial,
            sortable: false,
            width: '8rem'

        },

        {
            name: "Contact",
            selector: (row: any) => row.bdmFirstName,
            sortable: false,
            width: '10rem'
        },
        {
            name: "Surname",
            selector: (row: any) => row.bdmLastName,
            sortable: false,
            width: '9rem'
        },
        {
            name: "Mobile",
            selector: (row: any) => formatMobile(row.bdmMobile),
            sortable: false,
            width: '10rem'
        },
        {
            name: "States",
            cell: (row: any) => {
                const stateIds = row.statecheckbox?.split(',') || [];

                const stateNames = stateIds
                    .map((id: string) => {
                        const match = statesData.find(
                            (state: any) => String(state.value) === id.trim()
                        ) as { label: string; value: string } | undefined;
                        return match?.label;
                    })
                    .filter(Boolean);

                const fullText = stateNames.join(', ');
                const displayText = buildTruncatedStatesText(stateNames, STATES_DISPLAY_CHAR_LIMIT);

                return <span title={fullText} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{displayText}</span>;
            },
            sortable: false,
            wrap: true,
            width: '16rem'
        },
        {
            name: "Method",
            selector: (row: any) => row.method || "",
            sortable: false,
            width: '10rem'
        },
        {
            name: "Followup",
            selector: (row: any) => formatFollowupDate(row.followUp),
            sortable: false,
            width: '9rem'
        },
        {
            name: "Email",
            selector: (row: any) => row.bdmEmail,
            sortable: false,
            width: '20rem'
        },
    ];

    return (
        <div className="px-4 pt-4" style={{ minHeight: '500px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            <div style={{ borderRadius: '8px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', padding: '10px', backgroundColor: '#fff' }}>
                <div className="g-3 mb-2 d-flex justify-content-between align-items-center">
                    <h3 className="f-w-900 heading-color mx-3 py-2">Real Estate Builder List</h3>
                    <div className="d-flex gap-2 mx-3">
                        <Button color="primary" outline onClick={handleSortFollowup}>
                            Sort Followup {sortFollowupDirection === 'asc' ? '▲' : '▼'}
                        </Button>
                        <Button color="primary" outline onClick={handleSortAZ}>
                            Sort A - Z {sortAZDirection === 'asc' ? '▲' : '▼'}
                        </Button>
                        <Button color="primary" onClick={() => navigate("/AddBuilder")}>Add Builder</Button>
                    </div>
                </div>

                {/* 5. Enhanced loading state handling */}
                {isLoading ? (
                    <div
                        style={{
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            padding: '12px',
                            backgroundColor: '#fff',
                            textAlign: 'center',
                        }}
                        className='mt-4 mb-2'
                    >
                        <Spinner color="primary"></Spinner>
                    </div>
                ) : builders.length > 0 ? (
                    // <div style={{ maxHeight: '500px', overflowY: 'auto' }}>

                    <DataTable
                        columns={columns}
                        data={builders}
                        // pagination
                        responsive
                        customStyles={customStyles}
                        highlightOnHover
                    />

                    // </div>
                ) : (
                    <div
                        style={{
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            padding: '12px',
                            backgroundColor: '#fff',
                            textAlign: 'center',
                        }}
                        className='mt-4 mb-2'
                    >
                        <h6>No Builders data available.</h6>
                    </div>
                )}

            </div>

            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)} centered>
                <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>Confirm Delete</ModalHeader>
                <ModalBody>Are you sure you want to delete this builder?</ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setDeleteModal(false)}>Cancel</Button>
                    <Button color="danger" onClick={handleDeleteClick} disabled={isDeleting}>
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default CRMBuilders;